/**
 * TabList / Tab — une barre d'onglets navigable au clavier.
 *
 * @remarks
 * Le motif « tabs » du WAI-ARIA : une seule entrée Tab dans la barre (l'onglet
 * actif, ou le premier s'il n'y en a pas), puis les flèches passent d'un onglet
 * à l'autre, Home et End vont aux extrémités. L'activation est automatique :
 * la flèche déplace le focus **et** sélectionne, via le `onClick` de l'onglet —
 * le caller n'a qu'un seul chemin à câbler, la souris et le clavier y passent
 * tous les deux.
 *
 * Avant SAAS-1187, les trois barres (shell, centre Skill-Up, analyse publique
 * d'un CV) posaient `role="tab"` sans rien derrière : chaque onglet était un
 * arrêt Tab, et les flèches ne faisaient rien.
 *
 * - `icon` : un carré à icône, les rails latéraux ;
 * - `editor` : un onglet à libellé, façon éditeur — c'est le caller qui pose
 *   sa largeur et sa croix ;
 * - `segment` : une pastille dans un rail arrondi, pour basculer entre les
 *   sections d'une page (la Boutique, SAAS-1346). Le rail est au caller.
 *
 * Le panneau (`role="tabpanel"`) n'est pas câblé : dans le shell, le contenu
 * est rendu par `Panel`, loin de la barre.
 */

import {
  forwardRef,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
} from 'react';
import { cn } from '@/lib/utils/cn';

const TAB_SELECTOR = '[role="tab"]:not(:disabled)';

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
}

export function TabList({
  orientation = 'horizontal',
  onKeyDown,
  className,
  children,
  ...rest
}: TabListProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Une seule entrée Tab : l'onglet sélectionné, sinon le premier. Recalculé à
  // chaque rendu — c'est le parent qui change la sélection, donc la barre est
  // rendue avec lui — et posé sur le DOM : React ne sait pas, onglet par
  // onglet, si un autre est sélectionné.
  useLayoutEffect(() => {
    const tabs = [...(ref.current?.querySelectorAll<HTMLElement>(TAB_SELECTOR) ?? [])];
    const entry = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? tabs[0];
    for (const tab of tabs) tab.setAttribute('tabindex', tab === entry ? '0' : '-1');
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;

    const tabs = [...e.currentTarget.querySelectorAll<HTMLElement>(TAB_SELECTOR)];
    const index = tabs.indexOf(e.target as HTMLElement);
    if (index === -1) return;

    const [prev, next] =
      orientation === 'horizontal' ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown'];
    let target: number;
    if (e.key === next) target = (index + 1) % tabs.length;
    else if (e.key === prev) target = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') target = 0;
    else if (e.key === 'End') target = tabs.length - 1;
    else return;

    e.preventDefault();
    const tab = tabs[target];
    if (!tab || target === index) return;
    tab.focus();
    tab.click();
  };

  return (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- composite ARIA : le focus est sur les onglets (tabindex itinérant), la barre ne fait qu'écouter les flèches qui en remontent.
    <div
      ref={ref}
      role="tablist"
      aria-orientation={orientation}
      onKeyDown={handleKeyDown}
      className={className}
      {...rest}
    >
      {children}
    </div>
  );
}

type TabVariant = 'icon' | 'editor' | 'segment';

const VARIANTS: Record<TabVariant, { base: string; selected: string; idle: string }> = {
  icon: {
    base: 'relative inline-flex size-9 items-center justify-center rounded-sm border border-transparent transition-all duration-(--transition-fast) active:scale-[0.96]',
    selected:
      'text-primary after:bg-primary border-(--color-accent-border) bg-(--color-accent-bg) after:absolute after:bottom-[3px] after:left-1/2 after:h-0.5 after:w-3.5 after:-translate-x-1/2 after:rounded-full',
    idle: 'text-fg-subtle hover:bg-bg-hover hover:text-fg-secondary hover:scale-[1.08]',
  },
  editor: {
    base: 'group relative flex shrink-0 items-center gap-1.5 border-r border-(--color-border) px-3 py-2.5 text-xs transition-colors',
    selected:
      'bg-bg text-fg after:bg-primary after:absolute after:inset-x-0 after:bottom-[-1px] after:h-0.5',
    idle: 'text-fg-secondary hover:bg-bg-hover hover:text-fg',
  },
  segment: {
    base: 'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
    // Le contraste du bouton plein (§ Boutons) : l'accent pur sous du blanc n'atteint pas 4,5:1.
    selected: 'bg-primary-strong text-on-primary shadow-sm',
    idle: 'text-fg-secondary hover:bg-bg-hover hover:text-fg',
  },
};

export interface TabProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected: boolean;
  variant?: TabVariant;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab(
  { selected, variant = 'icon', className, ...rest },
  ref,
) {
  const styles = VARIANTS[variant];
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      className={cn(styles.base, selected ? styles.selected : styles.idle, className)}
      {...rest}
    />
  );
});
